import React, { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader, fmtNum, SelisihBadge } from "../components/Shared";
import { toast } from "sonner";
import {
  History, Loader2, ChevronRight, ArrowLeft, FileSpreadsheet, FileText, Calendar, Warehouse, Trash2,
} from "lucide-react";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
  } catch (e) { return d; }
}

export default function Riwayat() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {date, lokasi}
  const [detail, setDetail] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opname/sessions");
      setSessions(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openSession = async (s) => {
    setActive(s);
    setDetailLoading(true);
    try {
      const { data } = await api.get("/opname/session", { params: { date: s.date, lokasi: s.lokasi } });
      setDetail(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setDetailLoading(false); }
  };

  const exportSession = async (type) => {    setBusy(type);
    try {
      const res = await api.get("/opname/session/export", {
        params: { date: active.date, lokasi: active.lokasi, type },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Riwayat_${active.date}.${type === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Riwayat ${type === "excel" ? "Excel" : "PDF"} diunduh`);
    } catch (e) { toast.error("Gagal mengekspor"); }
    finally { setBusy(""); }
  };

  const deleteSession = async (s, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Hapus riwayat "${s.lokasi}" tanggal ${fmtDate(s.date)}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete("/opname/session", { params: { date: s.date, lokasi: s.lokasi } });
      toast.success("Riwayat opname dihapus");
      if (active && active.date === s.date && active.lokasi === s.lokasi) { setActive(null); setDetail([]); }
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  if (active) {
    return (
      <div className="pb-12">
        <PageHeader title="Detail Riwayat Opname" subtitle={`${active.lokasi} · ${fmtDate(active.date)}`}>
          <button onClick={() => exportSession("excel")} disabled={busy} data-testid="riwayat-export-excel" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {busy === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
          </button>
          <button onClick={() => exportSession("pdf")} disabled={busy} data-testid="riwayat-export-pdf" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </button>
          <button onClick={() => deleteSession(active)} data-testid="riwayat-delete-detail" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3.5 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Hapus Riwayat
          </button>
        </PageHeader>

        <div className="px-6 lg:px-8 pt-6 space-y-4">
          <button onClick={() => { setActive(null); setDetail([]); }} data-testid="riwayat-back-button" className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800">
            <ArrowLeft className="h-4 w-4" /> Kembali ke daftar riwayat
          </button>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left">
                    {["Nama Obat", "Satuan", "Qty Awal", "Qty Fisik", "Selisih", "No Batch", "Kadaluarsa", "Petugas", "Keterangan"].map((h, i) => (
                      <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailLoading ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                  ) : detail.map((m, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid="riwayat-detail-row">
                      <td className="px-4 py-2.5 font-medium text-slate-800 max-w-xs">{m.nama}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                      <td className="px-4 py-2.5 text-right tabular font-mono-t text-slate-700">{fmtNum(m.quantity_awal)}</td>
                      <td className="px-4 py-2.5 text-right tabular font-mono-t text-slate-700">{fmtNum(m.quantity_fisik)}</td>
                      <td className="px-4 py-2.5"><SelisihBadge value={m.selisih} /></td>
                      <td className="px-4 py-2.5 text-xs font-mono-t text-slate-500">{m.batch || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{m.tanggal_expired || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{m.user_name || "-"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{m.keterangan || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <PageHeader title="Riwayat Opname" subtitle="Sesi tersimpan otomatis per tanggal & tempat penyimpanan" />
      <div className="px-6 lg:px-8 pt-6">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            <History className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Belum ada riwayat. Simpan hitungan di Stock Opname untuk membuat riwayat.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map((s, i) => (
              <div key={i} className="relative text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all group">
                <button
                  onClick={(e) => deleteSession(s, e)}
                  data-testid="riwayat-delete-card"
                  title="Hapus riwayat"
                  className="absolute top-3 right-3 z-10 p-1.5 rounded-md text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <button onClick={() => openSession(s)} data-testid="riwayat-session-card" className="w-full text-left">
                  <div className="flex items-center gap-2 text-teal-700">
                    <Calendar className="h-4 w-4" />
                    <span className="font-head font-bold text-slate-900">{fmtDate(s.date)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><Warehouse className="h-3.5 w-3.5" /> {s.lokasi || "-"}</p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex gap-3 text-xs">
                      <span className="text-slate-600">{fmtNum(s.total)} item</span>
                      {s.minus > 0 && <span className="text-red-600 font-semibold">-{s.minus} minus</span>}
                      {s.plus > 0 && <span className="text-amber-600 font-semibold">+{s.plus} plus</span>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-teal-600 transition-colors" />
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
